import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSendRepairPartsToPaint, useUpdateRepairPartsStatus } from '@/api/hooks/useRepairParts'
import type { WorkOrder } from '@/api/hooks/useWorkOrder'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { asyncToast } from '@/components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { TranslationKey } from '@/i18n/types'
import { isSelectableForAction, REPAIR_STATUS_LABELS, type RepairAction } from '@/lib/repair-parts'

type Props = {
  workOrder: WorkOrder
  open: boolean
  onOpenChange: (open: boolean) => void
  // Preset the action (e.g. when opened from a board card), skipping the choose step.
  initialAction?: RepairAction
}

const ACTION_LABELS = {
  send: 'repairParts.action.send',
  missing: 'repairParts.action.missing',
} as const satisfies Record<RepairAction, TranslationKey>

const TOAST_KEYS = {
  send: {
    loading: 'repairParts.toast.send.loading',
    success: 'repairParts.toast.send.success',
    error: 'repairParts.toast.send.error',
  },
  missing: {
    loading: 'repairParts.toast.missing.loading',
    success: 'repairParts.toast.missing.success',
    error: 'repairParts.toast.missing.error',
  },
} as const satisfies Record<
  RepairAction,
  { loading: TranslationKey; success: TranslationKey; error: TranslationKey }
>

const ACTIONS: RepairAction[] = ['send', 'missing']

const RepairPartsActionDialog = ({ workOrder, open, onOpenChange, initialAction }: Props) => {
  const { t } = useTranslation()

  const [action, setAction] = useState<RepairAction | null>(initialAction ?? null)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const sendMutation = useSendRepairPartsToPaint(workOrder.id)
  const statusMutation = useUpdateRepairPartsStatus(workOrder.id)
  const isPending = sendMutation.isPending || statusMutation.isPending

  // Reset to the initial step each time the dialog opens.
  useEffect(() => {
    if (open) {
      setAction(initialAction ?? null)
      setSelected(new Set())
    }
  }, [open, initialAction])

  const candidates = useMemo(
    () =>
      action
        ? (workOrder.repairParts ?? []).filter(part => isSelectableForAction(part, action))
        : [],
    [action, workOrder.repairParts],
  )

  const allSelected = candidates.length > 0 && candidates.every(part => selected.has(part.id))

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(candidates.map(part => part.id)))

  const handleConfirm = async () => {
    if (!action) return
    const partIds = candidates.filter(part => selected.has(part.id)).map(part => part.id)
    if (partIds.length === 0) return

    const run = () => {
      if (action === 'send') {
        return sendMutation.mutateAsync({ path: { id: workOrder.id }, body: { partIds } })
      }
      return statusMutation.mutateAsync({
        body: { items: partIds.map(id => ({ id, status: 'missing' as const })) },
      })
    }

    await asyncToast(run(), {
      loading: () => [t(TOAST_KEYS[action].loading)],
      success: () => [t(TOAST_KEYS[action].success)],
      error: () => [t(TOAST_KEYS[action].error)],
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {action ? t(ACTION_LABELS[action]) : t('repairParts.action.button')}
          </DialogTitle>
          {action && <DialogDescription>{t('repairParts.action.selectParts')}</DialogDescription>}
        </DialogHeader>

        {!action ? (
          <div className="flex flex-col gap-2 py-2">
            {ACTIONS.map(a => (
              <Button
                key={a}
                variant="outline"
                className="justify-start"
                onClick={() => setAction(a)}
              >
                {t(ACTION_LABELS[a])}
              </Button>
            ))}
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-0">
                    <Checkbox
                      checked={allSelected}
                      disabled={candidates.length === 0}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead className="w-full">{t('repairParts.column.name')}</TableHead>
                  <TableHead>{t('repairParts.column.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map(part => {
                  const isSelected = selected.has(part.id)
                  return (
                    <TableRow key={part.id} data-clickable={true} onClick={() => toggle(part.id)}>
                      <TableCell onClick={event => event.stopPropagation()}>
                        <Checkbox checked={isSelected} onCheckedChange={() => toggle(part.id)} />
                      </TableCell>
                      <TableCell className="w-full">{part.name}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {t(REPAIR_STATUS_LABELS[part.status])}
                      </TableCell>
                    </TableRow>
                  )
                })}
                {candidates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      {t('repairParts.action.noSelectable')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          {action && !initialAction && (
            <Button variant="ghost" onClick={() => setAction(null)} disabled={isPending}>
              {t('repairParts.action.back')}
            </Button>
          )}
          {action && (
            <Button
              onClick={handleConfirm}
              isLoading={isPending}
              disabled={selected.size === 0 || isPending}
            >
              {t('repairParts.action.confirm')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default RepairPartsActionDialog
